<template>
  <div class="bg-brand-900 flex flex-col gap-5 items-center p-8 rounded-2xl flex-1 min-w-[285px] md:min-w-0 md:w-full">
    <!-- Icon -->
    <div
      v-if="icon"
      class="border border-grey-dark-300 rounded-[10px] custom-shadow w-12 h-12 flex items-center justify-center"
    >
      <img v-if="typeof icon === 'string'" :src="icon" alt="" class="w-6 h-6" />
      <component v-else :is="icon" class="w-6 h-6 text-grey-dark-300" />
    </div>

    <!-- Text Content -->
    <div class="flex flex-col flex-1 gap-2 items-center text-center w-full">
      <h3 class="text-xl font-semibold leading-[30px] text-base-white">{{ title }}</h3>
      <p v-if="description" class="text-base font-normal leading-6 text-grey-dark-300">
        {{ description }}
      </p>
    </div>

    <!-- Button -->
    <component
      v-if="hasButton"
      :is="isExternalLink ? 'a' : 'router-link'"
      :href="isExternalLink ? buttonLink : undefined"
      :to="isExternalLink ? undefined : buttonLink || '#'"
      :target="isExternalLink ? '_blank' : undefined"
      :rel="isExternalLink ? 'noopener noreferrer' : undefined"
      class="mt-6"
    >
      <Button :variant="buttonStyle" size="business" class="w-full md:w-auto">
        {{ buttonLabel }} <ArrowRight v-if="buttonStyle === 'link' || buttonStyle === 'link-light'" />
      </Button>
    </component>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-vue-next';
import { computed, type Component } from 'vue';

interface Props {
  icon?: string | Component;
  title: string;
  description: string;
  hasButton?: boolean;
  buttonLabel?: string;
  buttonStyle?:
    | 'outline-light'
    | 'default'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'link-light'
    | 'greyed'
    | 'secondary';
  buttonLink?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hasButton: false,
  buttonLabel: 'Learn more',
  buttonStyle: 'outline-light',
});

const isExternalLink = computed(() => {
  return props.buttonLink?.startsWith('http://') || props.buttonLink?.startsWith('https://');
});
</script>

<style scoped>
.custom-shadow {
  @apply shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)];
}
</style>
